import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProductReport } from "../../redux/apiCalls";
import { publicRequest } from "../../requestMethods";
import moment from "moment";
import excel from "../../components/excel/excel";
import Select from "react-select";
import { useHistory } from "react-router";
import PageHeader from "../../components/pageHeader/PageHeader";
import Table from "../../components/advancedTable";
import "./productReport.css";
import {
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Bar,
    ResponsiveContainer,
    BarChart,
    PieChart,
    Pie,
    Cell,
} from "recharts";

const getIntroOfPage = (label) => {
    if (label === "Page A") {
        return "Page A is about men's clothing";
    }
    if (label === "Page B") {
        return "Page B is about women's dress";
    }
    if (label === "Page C") {
        return "Page C is about women's bag";
    }
    if (label === "Page D") {
        return "Page D is about household goods";
    }
    if (label === "Page E") {
        return "Page E is about food";
    }
    if (label === "Page F") {
        return "Page F is about baby food";
    }
    return "";
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div
                className="custom-tooltip p-8 font-bold bg-black"
                style={{ background: payload[0].payload.fill }}
            >
                <p className="bold label">{`${payload[0].payload.outlet} : ${payload[0].value} Units`}</p>
                <p className="intro">{getIntroOfPage(label)}</p>
            </div>
        );
    }

    return null;
};

export default function OutletReport() {
    const dispatch = useDispatch();
    const history = useHistory();
    const currentUser = useSelector((state) => state.user.currentUser);

    const COLORS = [
        "#0088FE",
        "#00C49F",
        "#FFBB28",
        "#FF8042",
        "#8042ff",
        "#ff4263",
        "#56462a",
        "#2a5646",
        "#f8edeb",
        "#df99ad",
        "#ffff00",
    ];

    const NumberFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const manager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "manager"
            : null
    );

    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin : null
    );

    const principle = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.principle : null
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [inputs, setInputs] = useState({
        createdBy: currentUser._id,
        principle: manager
            ? principle
            : currentUser
            ? currentUser._id
            : undefined,
        dateFrom: moment(Date.now()).startOf("year").format("YYYY-MM-DD"),
        dateTo: moment(Date.now()).add(1, "days").format("YYYY-MM-DD"),
    });
    const [isMobileOrTablet, setIsMobileOrTablet] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth <= 1024 : false
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobileOrTablet(window.innerWidth <= 1024);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getAdmin = async () => {
        const res =
            process.env.REACT_APP_COUNTRY === "SINGAPORE"
                ? await publicRequest.get("/users/admin", {
                      params: { isAdmin: true },
                  })
                : await publicRequest.get("/users/superadmin", {
                      params: { isAdmin: true },
                  });
        setUsersData(res.data);
    };

    const handleChange = (e) => {
        setInputs((prev) => {
            return { ...prev, user: e.value };
        });
    };

    const handleDateFrom = (e) => {
        setInputs((prev) => {
            return { ...prev, dateFrom: e.target.value };
        });
    };
    const handleDateTo = (e) => {
        setInputs((prev) => {
            return { ...prev, dateTo: e.target.value };
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            const res = await getProductReport(dispatch, inputs);

            if (res) {
                setIsLoaded(true);
                setData(res.product);
                setSalesData(res.sales);
            }
        };
        fetchData();
    }, [dispatch, inputs]);

    useEffect(() => {
        if (usersData[0] === undefined) {
            getAdmin();
        }
    }, [usersData]);

    const columns = [
        {
            accessor: "outlet",
            Header: "Outlet",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding">{value ? value : ""}</div>
            ),
        },
        {
            accessor: "amount",
            Header: "Amount",
            sortable: false,
            Cell: (params) => (
                <div className="capitalize">
                    {params.row.original
                        ? params.row.original.outlet === "HAOFA Tourbillon Singapore"
                            ? "$"
                            : "RM"
                        : "RM"}
                    {NumberFormatter.format(
                        params.row.original ? (params.row.original.amount ? params.row.original.amount : 0) : 0
                    )}
                </div>
            ),
        },
        {
            accessor: "unit",
            Header: "Unit",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding">{value ? value : 0}</div>
            ),
        },
        {
            accessor: "action",
            Header: "Action",
            sortable: false,
            Cell: (params) => {
                return (
                    <>
                        {superadmin && (
                            <>
                                <div
                                    style={{
                                        textDecoration: "none",
                                        color: "black",
                                    }}
                                    onClick={() =>
                                        history.push("/orders", {
                                            outlet: params.row.original
                                                ? params.row.original.outletId
                                                : undefined,
                                        })
                                    }
                                >
                                    <button className="productListEdit">
                                        View Sales
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                );
            },
        },
    ];

    return (
        <>
            {!data[0] && (
                      <div className="productList">
                            <PageHeader 
                                title="Product Report" 
                                description="Analyze product sales performance and outlet statistics" 
                            />
                                                    <div className="samerow">
                                <div className="addProductItem">
                                    <input
                                        className="inputFilter"
                                        name="dateFrom"
                                        value={inputs.dateFrom}
                                        type="date"
                                        onChange={(e) => handleDateFrom(e)}
                                    />
                                </div>
                                <div className="addProductItem">
                                    <input
                                        className="inputFilter"
                                        name="dateTo"
                                        value={inputs.dateTo}
                                        type="date"
                                        onChange={(e) => handleDateTo(e)}
                                    />
                                </div>
                                <div className="sameRowItem marginRight">
                                    {superadmin && (
                                        <Select
                                            options={usersData.map(
                                                (user, i) => {
                                                    return {
                                                        label: `${user.name} - ${user.phoneNumber} - ${user.email}`,
                                                        value: user._id,
                                                    };
                                                }
                                            )}
                                            onChange={(e) => handleChange(e)}
                                            placeholder={"Outlet"}
                                            onClick={() =>
                                                usersData[0] === undefined
                                                    ? getAdmin()
                                                    : {}
                                            }
                                        />
                                    )}
                                </div>
                            </div> <h1>
                      No Sales has been made this month yet. Please check back
                      later.
                  </h1></div>
               
            )}
                  {data[0] && (
            <div className="productList">
                <PageHeader 
                    title="Product Report" 
                    description="Analyze product sales performance and outlet statistics" 
                />
                {window.location.pathname !== "/" && (
                    <>
                        <div className="samerow">
                            <div className="addProductItem">
                                <input
                                    className="inputFilter mt-2"
                                    name="dateFrom"
                                    value={inputs.dateFrom}
                                    type="date"
                                    onChange={(e) => handleDateFrom(e)}
                                />
                            </div>
                            <div className="addProductItem">
                                <input
                                    className="inputFilter mt-2"
                                    name="dateTo"
                                    value={inputs.dateTo}
                                    type="date"
                                    onChange={(e) => handleDateTo(e)}
                                />
                            </div>
                            <div className="sameRowItem marginRight">
                                {superadmin && (
                                    <Select
                                        options={usersData.map((user, i) => {
                                            return {
                                                label: `${user.name} - ${user.phoneNumber} - ${user.email}`,
                                                value: user._id,
                                            };
                                        })}
                                        onChange={(e) => handleChange(e)}
                                        placeholder={"Outlet"}
                                        onClick={() =>
                                            usersData[0] === undefined
                                                ? getAdmin()
                                                : {}
                                        }
                                    />
                                )}
                            </div>
                        </div>
                        <div className="block">
                            <button
                                onClick={() => {
                                    const payload = salesData.map((item) => {
                                        return {
                                            Outlet: item.outlet,
                                            Unit: item.unit,
                                            Amount: item.amount,
                                        };
                                    });
                                    excel({
                                        excelData: payload,
                                        fileName: `Haofa-Sales-${moment(
                                            inputs.dateFrom
                                        ).format("DD-MM-YYYY")}-to-${moment(
                                            inputs.dateTo
                                        ).format("DD-MM-YYYY")}`,
                                    });
                                }}
                                className="red buttonTop productListButton"
                            >
                                Export
                            </button>
                        </div>
                    </>
                )}
                {isLoaded === true && (
                    <>
                        {data[0] && (
                            <div>
                                <div className="chartsContainer">
                                    <div className="barChartContainer">
                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >
                                            <BarChart
                                                width={500}
                                                height={500}
                                                stroke="#fff"
                                                fill="#fff"
                                                data={data}
                                                layout={
                                                    isMobileOrTablet
                                                        ? "vertical"
                                                        : "horizontal"
                                                }
                                                margin={{
                                                    top: 5,
                                                    right: 20,
                                                    left: 0,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid
                                                    stroke="#f5f5f5"
                                                    strokeDasharray="3 3"
                                                />
                                                {isMobileOrTablet ? (
                                                    <>
                                                        <YAxis
                                                            type="category"
                                                            dataKey="code"
                                                            width={120}
                                                        />
                                                        <XAxis type="number" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <XAxis dataKey="code" />
                                                        <YAxis stroke="#fff" />
                                                    </>
                                                )}
                                                <Tooltip />
                                                <Bar
                                                    dataKey="amount"
                                                    stackId="a"
                                                    fill="#1f355e"
                                                    label={{
                                                        position: isMobileOrTablet
                                                            ? "right"
                                                            : "top",
                                                    }}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="pieChartContainer">
                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >
                                            <PieChart>
                                                <Pie
                                                    data={salesData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label
                                                    outerRadius={100}
                                                    fill="#8884d8"
                                                    dataKey="unit"
                                                >
                                                    {salesData.map((entry, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={
                                                                COLORS[
                                                                    index %
                                                                        COLORS.length
                                                                ]
                                                            }
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={<CustomTooltip />} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                                <div className="tableContainer">
                                    <Table
                                        data={salesData ? salesData : []}
                                        columns={columns}
                                        pageCount={Math.floor((salesData ? salesData.length : 0) / 20) + 1}
                                        totalCount={salesData ? salesData.length : 0}
                                        hidePagination={true}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
                )}
        </>
    );
}
